import {
  AddOnApiPackage,
  AddOnApiVariant,
  OrderRequestAddonItem,
  OrderRequestAddonPackageKind,
  OrderRequestAddonVariantLine,
  OrderRequestVendingItem,
} from '../models/notification-order-request.models';
import { formatCheckoutCurrency } from './notification-currency.util';

function slugForAddonIdPart(value: string | undefined): string {
  const s = String(value ?? 'x')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return s || 'x';
}

function variantListPriceNumber(v: AddOnApiVariant): number | null {
  const raw = v.single_list_price;
  if (raw == null || raw === '') {
    return null;
  }
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function packageListPriceNumber(pkg: AddOnApiPackage): number | null {
  const raw = pkg.single_list_price;
  if (raw == null || raw === '') {
    return null;
  }
  const n = typeof raw === 'number' ? raw : Number(String(raw).replace(/[$,]/g, '').trim());
  return Number.isFinite(n) ? n : null;
}

function packageAllowsCustomPrice(pkg: AddOnApiPackage): boolean {
  const a = pkg.allows_custom_price;
  return a === true || Number(a) === 1 || String(a).toLowerCase() === 'true';
}

function variantQuantitySelectionFromApi(v: AddOnApiVariant): 'single' | 'multiple' {
  const s = String(v.quantity_selection ?? '')
    .toLowerCase()
    .trim();
  return s === 'multiple' ? 'multiple' : 'single';
}

function resolveOrderRequestAddonPackageKind(pkg: AddOnApiPackage): OrderRequestAddonPackageKind {
  const type = String(pkg.type ?? '')
    .toLowerCase()
    .trim();
  return type === 'detailing' ? 'detailing' : 'add_on';
}

function defaultOrderRequestAddonLabel(
  pkg: AddOnApiPackage,
  packageKind: OrderRequestAddonPackageKind
): string {
  const fromApi = (pkg.package_name || '').trim();
  if (fromApi) {
    return fromApi;
  }
  return packageKind === 'detailing' ? 'Detailing' : 'Add-on';
}

/** Detailing tiles in the shared Add on section reuse add-on `paired_with` rules (default: single wash). */
function resolveOrderRequestAddonPairedWith(
  pkg: AddOnApiPackage,
  packageKind: OrderRequestAddonPackageKind
): string[] | undefined {
  if (pkg.paired_with?.length) {
    return [...pkg.paired_with];
  }
  if (packageKind === 'detailing') {
    return ['single_wash'];
  }
  return undefined;
}

function isAddOnApiPackageVending(pkg: AddOnApiPackage): boolean {
  const t = (pkg.type || '').toLowerCase();
  if (t === 'vending') {
    return true;
  }
  const wts = (pkg.wash_types || []).map(w => String(w).toLowerCase());
  return wts.includes('vending');
}

function mapApiVariantsToVendingCatalog(pkg: AddOnApiPackage): OrderRequestVendingItem[] {
  const pkgKey = slugForAddonIdPart(pkg.id || pkg.package_name);
  const variants = (pkg.variants || []).filter(v => v.is_active !== false);
  return variants.map((v, index) => {
    const idPart = slugForAddonIdPart(v.id || v.variant_name) + '_' + index;
    const price = variantListPriceNumber(v) ?? 0;
    return {
      id: `vprod_${pkgKey}_${idPart}`,
      name: (v.variant_name || 'Item').trim() || 'Item',
      quantity: 0,
      unitPrice: price,
      quantitySelection: variantQuantitySelectionFromApi(v),
      allowsCustomPrice: v.allows_custom_price === true,
      apiVariantId: v.id != null && String(v.id).trim() ? String(v.id).trim() : undefined,
    };
  });
}

function mapVariantToAddonMode(v: AddOnApiVariant): 'custom_price' | 'fixed' {
  if (v.allows_custom_price) {
    return 'custom_price';
  }
  if (variantListPriceNumber(v) != null) {
    return 'fixed';
  }
  return 'custom_price';
}

/** Map `package/add-ons-by-location` rows to order-request add-on tiles. */
export function mapAddOnPackagesToOrderItems(packages: AddOnApiPackage[]): OrderRequestAddonItem[] {
  const items: OrderRequestAddonItem[] = [];
  for (const pkg of packages) {
    const packageKind = resolveOrderRequestAddonPackageKind(pkg);
    const allVariants = pkg.variants || [];
    const activeVariants = allVariants.filter(v => v.is_active !== false);
    if (allVariants.length > 0 && activeVariants.length === 0) {
      continue;
    }
    if (isAddOnApiPackageVending(pkg)) {
      const catalog = mapApiVariantsToVendingCatalog(pkg);
      if (catalog.length > 0) {
        const pkgKey = slugForAddonIdPart(pkg.id || pkg.package_name);
        const pkgLabel = defaultOrderRequestAddonLabel(pkg, packageKind);
        const pairedWith = resolveOrderRequestAddonPairedWith(pkg, packageKind);
        items.push({
          id: `addon_products_${pkgKey}`,
          label: pkgLabel,
          mode: 'choose_products',
          pairedWith,
          vendingCatalog: catalog,
          apiPackageId: pkg.id != null && String(pkg.id).trim() ? String(pkg.id).trim() : undefined,
          licensePlateNeeded: !!pkg.license_plate_needed,
          packageKind,
        });
        continue;
      }
    }
    if (!activeVariants.length) {
      const pkgKey = slugForAddonIdPart(pkg.id || pkg.package_name);
      const pkgLabel = defaultOrderRequestAddonLabel(pkg, packageKind);
      const pairedWith = resolveOrderRequestAddonPairedWith(pkg, packageKind);
      const pkgId = pkg.id != null && String(pkg.id).trim() ? String(pkg.id).trim() : undefined;
      if (!pkgId) {
        continue;
      }
      const listN = packageListPriceNumber(pkg);
      const allowCustom = packageAllowsCustomPrice(pkg);
      if (allowCustom) {
        items.push({
          id: `addon_${pkgKey}_no_variants`,
          label: pkgLabel,
          mode: 'custom_price',
          pairedWith,
          apiPackageId: pkgId,
          licensePlateNeeded: !!pkg.license_plate_needed,
          packageKind,
        });
      } else if (listN != null && Number.isFinite(listN) && listN >= 0) {
        items.push({
          id: `addon_${pkgKey}_no_variants`,
          label: pkgLabel,
          mode: 'fixed',
          fixedPrice: formatCheckoutCurrency(listN),
          pairedWith,
          apiPackageId: pkgId,
          licensePlateNeeded: !!pkg.license_plate_needed,
          packageKind,
        });
      } else {
        items.push({
          id: `addon_${pkgKey}_no_variants`,
          label: pkgLabel,
          mode: 'custom_price',
          pairedWith,
          apiPackageId: pkgId,
          licensePlateNeeded: !!pkg.license_plate_needed,
          packageKind,
        });
      }
      continue;
    }
    const pkgKey = slugForAddonIdPart(pkg.id || pkg.package_name);
    const pkgLabel = defaultOrderRequestAddonLabel(pkg, packageKind);
    const pairedWith = resolveOrderRequestAddonPairedWith(pkg, packageKind);

    if (activeVariants.length >= 2) {
      const lines: OrderRequestAddonVariantLine[] = [];
      activeVariants.forEach((v, index) => {
        const mode = mapVariantToAddonMode(v);
        const varKey = slugForAddonIdPart(v.id || v.variant_name) + '_' + index;
        const unit = variantListPriceNumber(v) ?? 0;
        const line: OrderRequestAddonVariantLine = {
          id: `addon_${pkgKey}_${varKey}`,
          label: (v.variant_name || 'Option').trim() || 'Option',
          mode,
          unitPrice: unit,
          quantitySelection: variantQuantitySelectionFromApi(v),
          apiVariantId: v.id != null && String(v.id).trim() ? String(v.id).trim() : undefined,
        };
        if (mode === 'fixed') {
          line.fixedPrice = formatCheckoutCurrency(unit);
        }
        lines.push(line);
      });
      items.push({
        id: `addon_pkg_${pkgKey}`,
        label: pkgLabel,
        mode: 'multi_variant_package',
        pairedWith,
        packageVariants: lines,
        apiPackageId: pkg.id != null && String(pkg.id).trim() ? String(pkg.id).trim() : undefined,
        licensePlateNeeded: !!pkg.license_plate_needed,
        packageKind,
      });
      continue;
    }

    const v = activeVariants[0];
    const mode = mapVariantToAddonMode(v);
    const varKey = slugForAddonIdPart(v.id || v.variant_name) + '_0';
    const row: OrderRequestAddonItem = {
      id: `addon_${pkgKey}_${varKey}`,
      label: pkgLabel,
      mode,
      pairedWith,
      apiPackageId: pkg.id != null && String(pkg.id).trim() ? String(pkg.id).trim() : undefined,
      apiVariantId: v.id != null && String(v.id).trim() ? String(v.id).trim() : undefined,
      licensePlateNeeded: !!pkg.license_plate_needed,
      packageKind,
    };
    if (mode === 'fixed') {
      const n = variantListPriceNumber(v) ?? 0;
      row.fixedPrice = formatCheckoutCurrency(n);
    }
    items.push(row);
  }
  return items;
}
